import { Theme } from "@mui/material/styles";
import { DiagnosticError, MediaConnectionBitrateTest } from "@twilio/rtc-diagnostics";
import { PreflightTest } from "@twilio/voice-sdk";
import { CustomerPortalTheme } from "Models/customerPortalTheme";
import { Category, Column } from "Components/AppGrid/shipeezi-new-grid";
import { CreateTaskPayload } from "./modules/moving/components/Diary/views/DiaryResourcesView/hooks/useDespatchActions";
import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import { Filters } from "Models/shipeezi.table";
import { StorageBox, StorageBoxDto } from "Models/storage.box";
import { Task } from "Models/task";
import { TaskComment } from "Models/task.comment";
import { LocalVideoTrack, RemoteVideoTrack, TwilioError } from "twilio-video";

import { CustomerUser } from "./models/customer.user";
import { NavBarLink } from "./models/nav.bar.link";
import { User } from "./models/user";
import { BusEventNames, ShipperType, TaskType } from "./util/enums";

export enum TestWarnings {
  none = "",
  warn = "warn",
  error = "error",
  warnTurn = "warnTurn",
  warnTurnTCP = "warnTurnTCP",
  warnTurnUDP = "warnTurnUDP",
}

export type Callback = (...args: any[]) => void;

export type EnterVideoSurvey = {
  roomNameOrEmailCustomer?: string;
  identity?: string;
  isSupportedTwilio?: boolean;
  isMobile?: boolean | null;
  browserName?: string | null;
  os?: string | null;
  browserVersion?: string | null;
  appVersion?: string;
  platform?: string;
};

export type ErrorCallback = (error: TwilioError | Error) => void;
declare module "twilio-video" {
  // These help to create union types between Local and Remote VideoTracks
  interface LocalVideoTrack {
    isSwitchedOff: undefined;
    setPriority: undefined;
  }
}

export type IVideoTrack = LocalVideoTrack | RemoteVideoTrack;

declare global {
  interface MediaDevices {
    getDisplayMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  }

  interface RTCIceServer {
    url: string;
  }

  interface HTMLMediaElement {
    // @ts-ignore
    setSinkId?(sinkId: string): Promise<void>;
  }

  // Helps create a union type with TwilioError
  interface Error {
    code: undefined;
    line?: number;
  }
}

export type NetworkTestName = "Bitrate Test" | "Preflight Test";

export type Edge =
  | "ashburn"
  | "dublin"
  | "frankfurt"
  | "roaming"
  | "sao-paulo"
  | "singapore"
  | "sydney"
  | "tokyo"
  | "ashburn-ix"
  | "london-ix"
  | "frankfurt-ix"
  | "san-jose-ix"
  | "singapore-ix";

interface TestError {
  code: number;
  info?: {
    code?: number;
    message?: string;
  };
  message: string;
  twilioError?: TwilioError;
}

export interface TestResults {
  edge: Edge;
  results: {
    bitrate?: MediaConnectionBitrateTest.Report;
    preflight?: PreflightTest.Report;
  };
  errors: {
    bitrate?: DiagnosticError;
    preflight?: TestError;
  };
}

export type ValueOf<T> = T[keyof T];

export type EmptyFunction = () => void;

export interface ViewOnly {
  fields?: boolean;
  actions?: boolean;
}

export interface QuoteStatuses {
  won?: number;
  lost?: number;
  cancelled?: number;
  draft?: number;
  pending?: number;
  inspect?: number;
  total?: number;
}

export interface ServiceProps {
  endpoint: string;
}

export type NavBarLinks = {
  [x: string]: Array<NavBarLink>;
};

export interface UserFields extends Partial<User> {
  isDateOfBirth?: Boolean;
  isFM?: Boolean;
  isProfileMoving?: Boolean;
}

export interface GenericSchema<T = any> extends Function {
  (entity?: T): T;
}

export interface GenericObject {
  [key: string]: any;
}
export interface ColumnWithKey<T = GenericObject> extends Omit<Column, "name"> {
  key?: string;
  name: keyof T;
}

export interface CategoryForLocalFilter extends Category {
  localKey?: string;
}

export interface RequestEscalation {
  jobId: number;
  userId?: number;
  comment?: string;
  toWhom?: string;
}

export interface ForgotPasswordCredentials {
  email: string;
  companyId: number;
}

export interface SubDomainInfo {
  name?: string;
  logo?: string;
  subdomain?: string;
  companyId?: number;
}

export interface CustomerUserResponseData {
  token: string;
  success: boolean;
  customerUser: CustomerUser;
  customerPortalTheme: CustomerPortalTheme;
}

export interface OrdersAssociatedCustomerUser {
  id?: number;
  reference?: string;
  status?: string;
  mode?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LanguagePrefix {
  code: string;
  language: string;
}

export type Shipper = {
  id?: number;
  fullName: string;
  type: ShipperType;
  code?: string;
};

declare module "@mui/styles/defaultTheme" {
  interface DefaultTheme extends Theme {}
}

interface CustomPalettes {
  grey: {
    main: string;
  };
}

declare module "@mui/material/styles" {
  interface PaletteOptions extends CustomPalettes {}
  interface Palette extends CustomPalettes {}
}
export interface TimesheetWidgets {
  workHours: string | null;
  breakHours: string | null;
  totalTimeHours: string | null;
  mostCommonTask: TaskType | null;
}

// Source: https://stackoverflow.com/a/71324882
export type UseState<S> = (action: S | ((prevState: S) => S)) => void;

export type BusEventPayloads = {
  [BusEventNames.DESPATCH_ON_SUCCESS_CANCEL_TASK]: {
    task: DespatchTask | Task;
    crew?: DespatchCrew | null;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_CREATE_COMMENT]: {
    comment: TaskComment;
    task: DespatchTask | Task;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_DELETE_COMMENT]: {
    commentId: TaskComment["id"];
    task: DespatchTask | Task;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_CREATE_CREATE_SINGLE_USE_BOX]: {
    storageBox: StorageBox;
    storageBoxDTO?: StorageBoxDto;
    payload: DespatchCrew;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_CREATE_TASK]: {
    task: Task;
    taskDTO?: CreateTaskPayload;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_CREATE_PLACEHOLDER_TASK]: {
    task: Task;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_DELETE_PLACEHOLDER_TASK]: {
    taskId: number;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_UPDATE_TASK]: {
    task: Task;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_UPDATE_TIME]: {
    task: Task;
  };
  [BusEventNames.DESPATCH_ON_SUCCESS_CREATE_CREW]: {
    crew: DespatchCrew;
  };
};

export interface ICategory extends Category {
  filterKey?: string;
}

export type IFilterBy = {
  onChange?: (filters: Filters) => void;
  categories?: ICategory[];
};
